// «I miei preferiti», connected: the folder «Preferiti» first (the pinned
// sources, chosen with the pin in Ultime news: no action here), then one
// folder per feed of the user (GET /me/feeds); the chosen one open with its
// sources as the rows of the sources panel; «Nuovo gruppo fonti», «Modifica» and
// «Elimina» through the three mutations, each in place in the panel. The
// design has the folders and the rows; the actions, the editor and the
// confirmation are built from Platea's own elements (see the report of the
// chapter).
import { useMemo, useState } from "react";
import { Button } from "@heroui/react";
import { useSearchParams } from "react-router";

import type { Source, SubSource } from "../../data/mock";
import { PanelStatus, STATUS_BUTTON_CLASS } from "../../components/PanelStatus";
import { PanelTitle, ScrollArea } from "../../components/sidebar/panels";
import { SidebarSection } from "../../components/sidebar/SidebarSection";
import { SourceList } from "../../components/sidebar/SourceList";
import { favouriteKeys, subtreeKeys, toSelection, toSourceTree } from "../news/adapt";
import { useSources } from "../news/api";
import { withNewsParams } from "../news/params";
import {
  FAVOURITES_FOLDER,
  feedChange,
  feedErrorMessage,
  nameProblem,
  newFeed,
  restrictTree,
  sortFeeds,
  type FeedChange,
  type NewFeed,
  type UserFeed,
} from "./adapt";
import { useChangeFeed, useCreateFeed, useDeleteFeed, useFeeds } from "./api";
import { FeedEditor, type FeedEditorError } from "./FeedEditor";

export const FEEDS_TITLE = "I miei preferiti";
const LOADING_FEEDS = "Caricamento dei feed…";
const FEEDS_FAILED = "Feed non disponibili.";
const FEEDS_REFRESH_FAILED = "Aggiornamento dei feed non riuscito.";
const LOADING_SOURCES = "Caricamento delle fonti…";
const SOURCES_FAILED = "Fonti non disponibili.";
const SOURCES_REFRESH_FAILED = "Aggiornamento delle fonti non riuscito.";
const NO_FAVOURITES = "Nessuna fonte preferita: usa il pin in Ultime news.";
const FAVOURITES_NOTE = "Le fonti si scelgono con il pin in Ultime news.";
const EMPTY_FEED = "Nessuna fonte nel feed: aggiungile con «Modifica».";
const CONFIRM_DELETE = "Eliminare il feed?";

/** What the panel is doing besides showing the feeds. */
type Mode =
  { kind: "idle" } | { kind: "create" } | { kind: "edit"; id: string } | { kind: "confirm-delete"; id: string };

/** The last refused change or deletion, shown under the feed it concerns with «Riprova» when it can be sent again. */
type Failure = { id: string; message: string; onRetry?: () => void };

type FeedsPanelProps = {
  /** The feed shown on the right (its folder is the open one); null for the folder «Preferiti». */
  selectedId: string | null;
  /** The URL names a feed: until the feeds have arrived no folder is the open one, «Preferiti» included. */
  wantsFeed: boolean;
  /** The user chose a folder (a feed's id, or FAVOURITES_FOLDER), or a new feed was created. */
  onSelect: (id: string) => void;
  /** The api confirmed a deletion: the feed leaves the URL if it is still the chosen one there. */
  onDeleted: (id: string) => void;
  /** Closes the panel: the chevron of the title (NewsLayout). */
  onCollapse: () => void;
};

/** What a query gives the panel: its data (even when a refetch failed), the wait, or the failure. */
const statusOf = (q: { data: unknown; isError: boolean; isFetching: boolean }) =>
  q.data !== undefined ? "ready" : q.isError && !q.isFetching ? "error" : "loading";

/** A refetch failed while the data already read stays on the page. */
const refreshFailed = (q: { data: unknown; isError: boolean; isFetching: boolean }) =>
  q.data !== undefined && q.isError && !q.isFetching;

export function FeedsPanel({ selectedId, wantsFeed, onSelect, onDeleted, onCollapse }: FeedsPanelProps) {
  const [search] = useSearchParams();
  const feedsQuery = useFeeds();
  const catalogue = useSources();
  const create = useCreateFeed();
  const change = useChangeFeed();
  const remove = useDeleteFeed();

  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const [failure, setFailure] = useState<Failure | null>(null);

  const feeds = useMemo(() => (feedsQuery.data ? sortFeeds(feedsQuery.data.feeds) : null), [feedsQuery.data]);
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);
  const favourites = useMemo(() => (catalogue.data ? favouriteKeys(catalogue.data) : null), [catalogue.data]);
  const favouriteSet = useMemo(() => new Set(favourites ?? []), [favourites]);
  const favouritesTree = useMemo(
    () => (tree && favourites ? restrictTree(tree.active, favourites) : []),
    [tree, favourites],
  );

  // a feed holds favourites only (the api drops an unpinned source): a key unpinned since the feeds were read is left out
  const sourcesOf = (feed: UserFeed) => (favourites ? feed.sources.filter((k) => favouriteSet.has(k)) : feed.sources);

  const linkTo = (item: Source | SubSource) => {
    const keys = subtreeKeys(item);
    return keys.length > 0 ? `/news?${withNewsParams(search, { tab: "latest", sources: keys })}` : null;
  };

  // the feed of the URL is not known yet (the feeds loading, or failed): «Preferiti» is not the open folder
  const feedPending = wantsFeed && feeds === null;
  const openFolder = feedPending ? null : (selectedId ?? FAVOURITES_FOLDER);

  const choose = (id: string) => {
    if (id === openFolder) return;
    // the editor and the confirmation belong to the open folder: choosing another one closes them
    if (mode.kind === "edit" || mode.kind === "confirm-delete") setMode({ kind: "idle" });
    onSelect(id);
  };

  const sendChange = (id: string, body: FeedChange) => {
    setFailure((f) => (f?.id === id ? null : f));
    change.mutateAsync({ id, change: body }).catch((error: unknown) => {
      const { message, retry } = feedErrorMessage(error, "change");
      setFailure({ id, message, ...(retry ? { onRetry: () => sendChange(id, body) } : {}) });
    });
  };

  // while the deletion is on its way the feed is out of the list, so the first one shows; the URL
  // keeps naming the deleted one until the api confirms, so a refused deletion brings it back as the chosen one
  const sendDelete = (id: string) => {
    setFailure((f) => (f?.id === id ? null : f));
    remove
      .mutateAsync(id)
      .then(() => onDeleted(id))
      .catch((error: unknown) => {
        const { message, retry } = feedErrorMessage(error, "delete");
        setFailure({ id, message, ...(retry ? { onRetry: () => sendDelete(id) } : {}) });
      });
  };

  const sendCreate = (body: NewFeed) => {
    create.mutate(body, {
      onSuccess: (feed) => {
        setMode({ kind: "idle" });
        onSelect(feed.id);
      },
    });
  };

  const createError: FeedEditorError | null = useMemo(() => {
    if (!create.isError || !create.variables) return null;
    const body = create.variables;
    const { message, retry } = feedErrorMessage(create.error, "create");
    return { message, ...(retry ? { onRetry: () => sendCreate(body) } : {}) };
    // sendCreate is a fresh closure at every render: the mutation's state is what matters here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create.isError, create.error, create.variables]);

  const feedsStatus = statusOf(feedsQuery);
  const sourcesStatus = statusOf(catalogue);
  const canCreate =
    feedsStatus === "ready" && sourcesStatus === "ready" && favouritesTree.length > 0 && mode.kind !== "create";

  // the rows of a folder (a feed's sources, or the favourites), or what stands in for them
  const rowsOf = (keys: string[], empty: string) => {
    if (sourcesStatus === "loading") return <PanelStatus>{LOADING_SOURCES}</PanelStatus>;
    if (sourcesStatus === "error" || !tree)
      return (
        <PanelStatus role="alert" onRetry={() => void catalogue.refetch()}>
          {SOURCES_FAILED}
        </PanelStatus>
      );
    const sources = restrictTree(tree.active, keys);
    if (sources.length === 0) return <PanelStatus>{empty}</PanelStatus>;
    // the folder's rows only show its sources: a click on a tick changes nothing (as in the design's
    // panel, the organisations are the read-only ticks, the sections keep the dark ones)
    return (
      <ScrollArea>
        <SourceList
          sources={sources}
          selection={toSelection({ active: sources }, keys)}
          onSelect={() => {}}
          readOnly
          linkTo={linkTo}
        />
      </ScrollArea>
    );
  };

  let list: React.ReactNode = null;
  if (feedsStatus === "loading") list = <PanelStatus>{LOADING_FEEDS}</PanelStatus>;
  else if (feedsStatus === "error")
    list = (
      <PanelStatus role="alert" onRetry={() => void feedsQuery.refetch()}>
        {FEEDS_FAILED}
      </PanelStatus>
    );

  return (
    <div className="flex flex-col gap-[30px]">
      <PanelTitle title={FEEDS_TITLE} onCollapse={onCollapse} />

      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          size="sm"
          isDisabled={!canCreate}
          onPress={() => {
            create.reset();
            setMode({ kind: "create" });
          }}
          className={STATUS_BUTTON_CLASS}
        >
          Nuovo gruppo fonti
        </Button>
      </div>

      {refreshFailed(feedsQuery) && (
        <PanelStatus role="alert" onRetry={() => void feedsQuery.refetch()}>
          {FEEDS_REFRESH_FAILED}
        </PanelStatus>
      )}
      {refreshFailed(catalogue) && (
        <PanelStatus role="alert" onRetry={() => void catalogue.refetch()}>
          {SOURCES_REFRESH_FAILED}
        </PanelStatus>
      )}

      <SidebarSection
        title="Preferiti"
        icon="folder"
        openIcon="folderOpen"
        expanded={openFolder === FAVOURITES_FOLDER}
        onExpandedChange={(next) => {
          // one folder is always open: closing the open one is not a choice
          if (next) choose(FAVOURITES_FOLDER);
        }}
      >
        <PanelStatus role="note">{FAVOURITES_NOTE}</PanelStatus>
        {rowsOf(favourites ?? [], NO_FAVOURITES)}
      </SidebarSection>

      {list}

      {mode.kind === "create" && (
        <SidebarSection
          title="Nuovo gruppo fonti"
          icon="folderOpen"
          expanded
          onExpandedChange={(open) => {
            if (!open && !create.isPending) setMode({ kind: "idle" });
          }}
        >
          <FeedEditor
            initialName=""
            initialSources={[]}
            favourites={favouritesTree}
            nameProblem={(name) => nameProblem(feeds ?? [], name)}
            saveLabel="Crea"
            pending={create.isPending}
            error={createError}
            onSave={(name, sources) => sendCreate(newFeed(name, sources))}
            onCancel={() => setMode({ kind: "idle" })}
          />
        </SidebarSection>
      )}

      {feeds?.map((feed) => {
        const open = feed.id === selectedId;
        const editing = mode.kind === "edit" && mode.id === feed.id;
        const confirming = mode.kind === "confirm-delete" && mode.id === feed.id;
        const failed = failure?.id === feed.id ? failure : null;
        return (
          <SidebarSection
            key={feed.id}
            title={feed.name}
            icon="folder"
            openIcon="folderOpen"
            expanded={open}
            onExpandedChange={(next) => {
              // one folder is always open: closing the open one is not a choice
              if (next) choose(feed.id);
            }}
          >
            {editing ? (
              <FeedEditor
                initialName={feed.name}
                initialSources={sourcesOf(feed)}
                favourites={favouritesTree}
                nameProblem={(name) => nameProblem(feeds, name, feed.id)}
                saveLabel="Salva"
                pending={false}
                error={null}
                onSave={(name, sources) => {
                  setMode({ kind: "idle" });
                  const body = feedChange({ name: feed.name, sources: sourcesOf(feed) }, name, sources);
                  if (body) sendChange(feed.id, body);
                }}
                onCancel={() => setMode({ kind: "idle" })}
              />
            ) : (
              <>
                {confirming ? (
                  <div role="note" className="flex flex-wrap items-center gap-2.5 py-2 text-sm leading-6 text-muted">
                    <span>{CONFIRM_DELETE}</span>
                    <Button size="sm" onPress={() => sendDelete(feed.id)} className={STATUS_BUTTON_CLASS}>
                      Elimina
                    </Button>
                    <Button size="sm" onPress={() => setMode({ kind: "idle" })} className={STATUS_BUTTON_CLASS}>
                      Annulla
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Button
                      size="sm"
                      isDisabled={sourcesStatus !== "ready"}
                      onPress={() => setMode({ kind: "edit", id: feed.id })}
                      className={STATUS_BUTTON_CLASS}
                    >
                      Modifica
                    </Button>
                    <Button
                      size="sm"
                      onPress={() => setMode({ kind: "confirm-delete", id: feed.id })}
                      className={STATUS_BUTTON_CLASS}
                    >
                      Elimina
                    </Button>
                  </div>
                )}
                {failed && (
                  <PanelStatus role="alert" onRetry={failed.onRetry}>
                    {failed.message}
                  </PanelStatus>
                )}
                {rowsOf(sourcesOf(feed), EMPTY_FEED)}
              </>
            )}
          </SidebarSection>
        );
      })}
    </div>
  );
}
